'use client';

import { BrandProductsStore } from '@/presentation/stores/BrandProductsStore';
import { GetBrandProductsUseCase } from '@/core/application/use-cases/product/GetBrandProductsUseCase';

import { GetCollectionUseCase } from '@/core/application/use-cases/product/GetCollectionUseCase';
import { CollectionStore } from '@/presentation/stores/CollectionStore';

// Repositories
import { WelcomeVideoStore } from '@/presentation/stores/WelcomeVideoStore';
import { QuickIntroHistoryStore } from '@/presentation/stores/QuickIntroHistoryStore';
import { QuickIntroBrowserHistory } from '@/core/infrastructure/services/QuickIntroBrowserHistory';
import type { IQuickIntroRepository } from '@/core/domain/repositories/IQuickIntroRepository';
import type { IUserRepository } from '@/core/domain/repositories/IUserRepository';
import type { IProductRepository } from '@/core/domain/repositories/IProductRepository';
import type { IOrderRepository } from '@/core/domain/repositories/IOrderRepository';

// Services
import type { IPaymentService } from '@/core/domain/services/IPaymentService';

// API-based implementations
import { ApiProductRepository } from '@/core/infrastructure/repositories/ApiProductRepository';
import { ApiOrderRepository } from '@/core/infrastructure/repositories/ApiOrderRepository';
import {
  ApiPaymentService,
  CheckoutSessionService,
  PSSCalculationService,
} from '@/core/infrastructure/services/ApiPaymentService';
import {
  ApiRepScoreRepository,
  type IRepScoreRepository,
  RepScoreService,
} from '@/core/infrastructure/services/RepScoreService';
import {
  ApiPay4MeRepository,
  type IPay4MeRepository,
  Pay4MeService,
} from '@/core/infrastructure/services/Pay4MeService';
import {
  type AuthTokenService,
  LocalStorageAuthTokenService,
} from '@/core/infrastructure/services/AuthTokenService';
import { PartnerAuthService } from '@/core/infrastructure/services/PartnerAuthService';
import { OTPAuthService } from '@/core/infrastructure/services/OTPAuthService';
import { AnonymousSessionService } from '@/core/infrastructure/services/AnonymousSessionService';
import { CampaignBootstrapService } from '@/core/infrastructure/services/CampaignBootstrapService';

// Use Cases
import { GetProductsUseCase } from '@/core/application/use-cases/product/GetProductsUseCase';
import { CreateOrderUseCase } from '@/core/application/use-cases/order/CreateOrderUseCase';
import { GetOrdersUseCase } from '@/core/application/use-cases/order/GetOrdersUseCase';
import { PartnerAuthenticationUseCase } from '@/core/application/use-cases/auth/PartnerAuthenticationUseCase';
import { OTPAuthenticationUseCase } from '@/core/application/use-cases/auth/OTPAuthenticationUseCase';
import { AnonymousSessionUseCase } from '@/core/application/use-cases/auth/AnonymousSessionUseCase';

// New Use Cases
import { GetPaymentPlansUseCase } from '@/core/application/use-cases/payment/GetPaymentPlansUseCase';
import { CreateCheckoutSessionUseCase } from '@/core/application/use-cases/checkout/CreateCheckoutSessionUseCase';
import { InitializePaymentUseCase } from '@/core/application/use-cases/payment/InitializePaymentUseCase';
import { GetUserRepScoreUseCase } from '@/core/application/use-cases/repScore/GetUserRepScoreUseCase';
import { CreatePay4MeCampaignUseCase } from '@/core/application/use-cases/pay4me/CreatePay4MeCampaignUseCase';

// Stores
import { ProductStore } from '@/presentation/stores/ProductStore';
import { OrderStore } from '@/presentation/stores/OrderStore';
import { AuthStore } from '@/presentation/stores/AuthStore';
import { PaymentStore } from '@/presentation/stores/PaymentStore';
import { CheckoutStore } from '@/presentation/stores/CheckoutStore';
import { ModalStore } from '@/presentation/stores/ModalStore';
import { RepScoreStore } from '@/presentation/stores/RepScoreStore';
import { Pay4MeStore } from '@/presentation/stores/Pay4MeStore';
import { ShoppingWalletStore } from '@/presentation/stores/ShoppingWalletStore';
import { UserRepository } from '@/core/infrastructure/repositories/UserRepository';
import { configure } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';

const shouldRequireReactiveReads = process.env.NODE_ENV !== 'development';

configure({
  enforceActions: 'observed',
  computedRequiresReaction: shouldRequireReactiveReads,
  reactionRequiresObservable: true,
  observableRequiresReaction: shouldRequireReactiveReads,
  disableErrorBoundaries: false,
});

// Enable static rendering for server-side rendering
enableStaticRendering(typeof window === 'undefined');

export interface IContainer {
  get<T>(key: string): T;

  // Repositories
  get userRepository(): IUserRepository;

  get productRepository(): IProductRepository;

  get orderRepository(): IOrderRepository;

  get repScoreRepository(): IRepScoreRepository;

  get pay4meRepository(): IPay4MeRepository;

  // Services
  get authTokenService(): AuthTokenService;

  get partnerAuthService(): PartnerAuthService;

  get otpAuthService(): OTPAuthService;

  get anonymousSessionService(): AnonymousSessionService;

  get campaignBootstrapService(): CampaignBootstrapService;

  get paymentService(): IPaymentService;

  get checkoutSessionService(): CheckoutSessionService;

  get pssCalculationService(): PSSCalculationService;

  get repScoreService(): RepScoreService;

  get pay4meService(): Pay4MeService;

  // Use Cases
  get getProductsUseCase(): GetProductsUseCase;

  get createOrderUseCase(): CreateOrderUseCase;

  get getOrdersUseCase(): GetOrdersUseCase;

  get partnerAuthenticationUseCase(): PartnerAuthenticationUseCase;

  get otpAuthenticationUseCase(): OTPAuthenticationUseCase;

  get anonymousSessionUseCase(): AnonymousSessionUseCase;

  // New Use Cases
  get getPaymentPlansUseCase(): GetPaymentPlansUseCase;

  get createCheckoutSessionUseCase(): CreateCheckoutSessionUseCase;

  get initializePaymentUseCase(): InitializePaymentUseCase;

  get getUserRepScoreUseCase(): GetUserRepScoreUseCase;

  get createPay4MeCampaignUseCase(): CreatePay4MeCampaignUseCase;

  // Stores
  get collectionStore(): CollectionStore;
  get brandProductsStore(): BrandProductsStore;
  get welcomeVideoStore(): WelcomeVideoStore;

  get productStore(): ProductStore;

  get orderStore(): OrderStore;

  get authStore(): AuthStore;

  get paymentStore(): PaymentStore;

  get checkoutStore(): CheckoutStore;

  get modalStore(): ModalStore;

  get repScoreStore(): RepScoreStore;

  get pay4meStore(): Pay4MeStore;

  get shoppingWalletStore(): ShoppingWalletStore;
}

export class Container implements IContainer {
  private static instance: Container;
  private baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? '/api/proxy';
  private baseProxyUrl =
    process.env.NEXT_PUBLIC_API_PROXY_BASE_URL ?? '/api/proxy';

  private constructor() {}

  // Repositories
  private _userRepository: IUserRepository | null = null;

  // Repository getters
  get userRepository(): IUserRepository {
    if (!this._userRepository) {
      this._userRepository = new UserRepository();
      // Lazy load repository on server side only
      // if (typeof window === 'undefined') {
      //
      // } else {
      //     throw new Error('Repository access not available on client side');
      // }
    }
    return this._userRepository!;
  }

  private _productRepository: (IProductRepository & IQuickIntroRepository) | null = null;

  get productRepository(): IProductRepository & IQuickIntroRepository {
    if (!this._productRepository) {
      this._productRepository = new ApiProductRepository(
        this.authTokenService,
        this.baseProxyUrl
      );
    }
    return this._productRepository;
  }

  private _orderRepository: IOrderRepository | null = null;

  get orderRepository(): IOrderRepository {
    if (!this._orderRepository) {
      this._orderRepository = new ApiOrderRepository(this.baseProxyUrl);
    }
    return this._orderRepository;
  }

  private _repScoreRepository: IRepScoreRepository | null = null;

  get repScoreRepository(): IRepScoreRepository {
    if (!this._repScoreRepository) {
      this._repScoreRepository = new ApiRepScoreRepository(
        this.repScoreService
      );
    }
    return this._repScoreRepository;
  }

  private _pay4meRepository: IPay4MeRepository | null = null;

  get pay4meRepository(): IPay4MeRepository {
    if (!this._pay4meRepository) {
      this._pay4meRepository = new ApiPay4MeRepository(this.pay4meService);
    }
    return this._pay4meRepository;
  }

  // Services
  private _authTokenService: AuthTokenService | null = null;

  get authTokenService(): AuthTokenService {
    if (!this._authTokenService) {
      this._authTokenService = new LocalStorageAuthTokenService();
    }
    return this._authTokenService;
  }

  private _partnerAuthService: PartnerAuthService | null = null;

  get partnerAuthService(): PartnerAuthService {
    if (!this._partnerAuthService) {
      this._partnerAuthService = new PartnerAuthService(
        this.authTokenService,
        this.baseProxyUrl
      );
    }
    return this._partnerAuthService;
  }

  private _otpAuthService: OTPAuthService | null = null;

  get otpAuthService(): OTPAuthService {
    if (!this._otpAuthService) {
      this._otpAuthService = new OTPAuthService(
        this.authTokenService,
        this.baseProxyUrl
      );
    }
    return this._otpAuthService;
  }

  private _anonymousSessionService: AnonymousSessionService | null = null;

  get anonymousSessionService(): AnonymousSessionService {
    if (!this._anonymousSessionService) {
      this._anonymousSessionService = new AnonymousSessionService(this.baseProxyUrl);
    }
    return this._anonymousSessionService;
  }

  private _campaignBootstrapService: CampaignBootstrapService | null = null;

  get campaignBootstrapService(): CampaignBootstrapService {
    if (!this._campaignBootstrapService) {
      this._campaignBootstrapService = new CampaignBootstrapService(
        this.authTokenService,
        this.baseProxyUrl
      );
    }
    return this._campaignBootstrapService;
  }

  private _paymentService: ApiPaymentService | null = null;

  get paymentService(): ApiPaymentService {
    if (!this._paymentService) {
      this._paymentService = new ApiPaymentService(this.baseProxyUrl, () => {
        return this._shoppingWalletStore?.loadWallet(true);
      });
    }
    return this._paymentService;
  }

  private _checkoutSessionService: CheckoutSessionService | null = null;

  get checkoutSessionService(): CheckoutSessionService {
    if (!this._checkoutSessionService) {
      this._checkoutSessionService = new CheckoutSessionService(
        this.baseProxyUrl
      );
    }
    return this._checkoutSessionService;
  }

  private _pssCalculationService: PSSCalculationService | null = null;

  get pssCalculationService(): PSSCalculationService {
    if (!this._pssCalculationService) {
      this._pssCalculationService = new PSSCalculationService();
    }
    return this._pssCalculationService;
  }

  private _repScoreService: RepScoreService | null = null;

  get repScoreService(): RepScoreService {
    if (!this._repScoreService) {
      this._repScoreService = new RepScoreService(
        this.authTokenService,
        this.baseProxyUrl
      );
    }
    return this._repScoreService;
  }

  private _pay4meService: Pay4MeService | null = null;

  get pay4meService(): Pay4MeService {
    if (!this._pay4meService) {
      this._pay4meService = new Pay4MeService(this.baseProxyUrl);
    }
    return this._pay4meService;
  }

  // Use Cases
  private _getProductsUseCase: GetProductsUseCase | null = null;

  get getProductsUseCase(): GetProductsUseCase {
    if (!this._getProductsUseCase) {
      this._getProductsUseCase = new GetProductsUseCase(this.productRepository);
    }
    return this._getProductsUseCase;
  }

  private _getPaymentPlansUseCase: GetPaymentPlansUseCase | null = null;

  get getPaymentPlansUseCase(): GetPaymentPlansUseCase {
    if (!this._getPaymentPlansUseCase) {
      this._getPaymentPlansUseCase = new GetPaymentPlansUseCase(
        this.productRepository,
        this.pssCalculationService,
        this.repScoreService
      );
    }
    return this._getPaymentPlansUseCase;
  }

  private _createCheckoutSessionUseCase: CreateCheckoutSessionUseCase | null =
    null;

  get createCheckoutSessionUseCase(): CreateCheckoutSessionUseCase {
    if (!this._createCheckoutSessionUseCase) {
      this._createCheckoutSessionUseCase = new CreateCheckoutSessionUseCase(
        this.checkoutSessionService,
        this.productRepository,
        this.userRepository
      );
    }
    return this._createCheckoutSessionUseCase;
  }

  private _initializePaymentUseCase: InitializePaymentUseCase | null = null;

  get initializePaymentUseCase(): InitializePaymentUseCase {
    if (!this._initializePaymentUseCase) {
      this._initializePaymentUseCase = new InitializePaymentUseCase(
        this.paymentService as ApiPaymentService,
        this.checkoutSessionService
      );
    }
    return this._initializePaymentUseCase;
  }

  private _getUserRepScoreUseCase: GetUserRepScoreUseCase | null = null;

  get getUserRepScoreUseCase(): GetUserRepScoreUseCase {
    if (!this._getUserRepScoreUseCase) {
      this._getUserRepScoreUseCase = new GetUserRepScoreUseCase(
        this.repScoreService
      );
    }
    return this._getUserRepScoreUseCase;
  }

  private _createPay4MeCampaignUseCase: CreatePay4MeCampaignUseCase | null =
    null;

  get createPay4MeCampaignUseCase(): CreatePay4MeCampaignUseCase {
    if (!this._createPay4MeCampaignUseCase) {
      this._createPay4MeCampaignUseCase = new CreatePay4MeCampaignUseCase(
        this.pay4meService,
        this.orderRepository
      );
    }
    return this._createPay4MeCampaignUseCase;
  }

  private _createOrderUseCase: CreateOrderUseCase | null = null;

  get createOrderUseCase(): CreateOrderUseCase {
    if (!this._createOrderUseCase) {
      this._createOrderUseCase = new CreateOrderUseCase(
        this.orderRepository,
        this.productRepository,
        this.userRepository,
        this.paymentService
      );
    }
    return this._createOrderUseCase;
  }

  private _getOrdersUseCase: GetOrdersUseCase | null = null;

  get getOrdersUseCase(): GetOrdersUseCase {
    if (!this._getOrdersUseCase) {
      this._getOrdersUseCase = new GetOrdersUseCase(this.orderRepository);
    }
    return this._getOrdersUseCase;
  }

  private _partnerAuthenticationUseCase: PartnerAuthenticationUseCase | null =
    null;

  get partnerAuthenticationUseCase(): PartnerAuthenticationUseCase {
    if (!this._partnerAuthenticationUseCase) {
      this._partnerAuthenticationUseCase = new PartnerAuthenticationUseCase(
        this.partnerAuthService,
        this.userRepository
      );
    }
    return this._partnerAuthenticationUseCase;
  }

  private _otpAuthenticationUseCase: OTPAuthenticationUseCase | null = null;

  get otpAuthenticationUseCase(): OTPAuthenticationUseCase {
    if (!this._otpAuthenticationUseCase) {
      this._otpAuthenticationUseCase = new OTPAuthenticationUseCase(
        this.otpAuthService,
        this.userRepository
      );
    }
    return this._otpAuthenticationUseCase;
  }

  private _anonymousSessionUseCase: AnonymousSessionUseCase | null = null;

  get anonymousSessionUseCase(): AnonymousSessionUseCase {
    if (!this._anonymousSessionUseCase) {
      this._anonymousSessionUseCase = new AnonymousSessionUseCase(
        this.anonymousSessionService
      );
    }
    return this._anonymousSessionUseCase;
  }

  // Stores
  private _brandProductsStore: BrandProductsStore | null = null;

  get brandProductsStore(): BrandProductsStore {
    if (!this._brandProductsStore) {
      this._brandProductsStore = new BrandProductsStore(new GetBrandProductsUseCase(this.productRepository));
    }
    return this._brandProductsStore;
  }

  private _collectionStore: CollectionStore | null = null;

  get collectionStore(): CollectionStore {
    if (!this._collectionStore) {
      this._collectionStore = new CollectionStore(new GetCollectionUseCase(this.productRepository));
    }
    return this._collectionStore;
  }

  private _productStore: ProductStore | null = null;

  get productStore(): ProductStore {
    if (!this._productStore) {
      this._productStore = new ProductStore(
        this.getProductsUseCase,
        this.getPaymentPlansUseCase,
        this.productRepository as any as ApiProductRepository
      );
    }
    return this._productStore;
  }

  private _orderStore: OrderStore | null = null;

  get orderStore(): OrderStore {
    if (!this._orderStore) {
      this._orderStore = new OrderStore(
        this.createOrderUseCase,
        this.getOrdersUseCase,
        this.createCheckoutSessionUseCase,
        this.initializePaymentUseCase,
        this.checkoutSessionService,
        this.paymentService as ApiPaymentService
      );
    }
    return this._orderStore;
  }

  private _authStore: AuthStore | null = null;

  get authStore(): AuthStore {
    if (!this._authStore) {
      this._authStore = new AuthStore(
        this.otpAuthenticationUseCase,
        this.partnerAuthService,
        this.otpAuthService,
        this.anonymousSessionService,
        this.campaignBootstrapService
      );
    }
    return this._authStore;
  }

  private _paymentStore: PaymentStore | null = null;

  get paymentStore(): PaymentStore {
    if (!this._paymentStore) {
      this._paymentStore = new PaymentStore(this, this.paymentService);
    }
    return this._paymentStore;
  }

  private _checkoutStore: CheckoutStore | null = null;

  get checkoutStore(): CheckoutStore {
    if (!this._checkoutStore) {
      this._checkoutStore = new CheckoutStore(
        this,
        this.productRepository,
        this.checkoutSessionService,
        this.paymentService
      );
    }
    return this._checkoutStore;
  }

  private _welcomeVideoStore: WelcomeVideoStore | null = null;

  get welcomeVideoStore(): WelcomeVideoStore {
    if (!this._welcomeVideoStore) {
      const storage = () => ({ local: window.localStorage, session: window.sessionStorage });
      this._welcomeVideoStore = new WelcomeVideoStore(storage, this.productRepository,
        new QuickIntroHistoryStore(new QuickIntroBrowserHistory(storage), this.productRepository));
    }
    return this._welcomeVideoStore;
  }

  private _modalStore: ModalStore | null = null;

  get modalStore(): ModalStore {
    if (!this._modalStore) {
      this._modalStore = new ModalStore();
    }
    return this._modalStore;
  }

  private _repScoreStore: RepScoreStore | null = null;

  get repScoreStore(): RepScoreStore {
    if (!this._repScoreStore) {
      this._repScoreStore = new RepScoreStore(
        this.getUserRepScoreUseCase,
        this.repScoreService
      );
    }
    return this._repScoreStore;
  }

  private _pay4meStore: Pay4MeStore | null = null;

  get pay4meStore(): Pay4MeStore {
    if (!this._pay4meStore) {
      this._pay4meStore = new Pay4MeStore(
        this.createPay4MeCampaignUseCase,
        this.pay4meService
      );
    }
    return this._pay4meStore;
  }

  private _shoppingWalletStore: ShoppingWalletStore | null = null;

  get shoppingWalletStore(): ShoppingWalletStore {
    if (!this._shoppingWalletStore) {
      this._shoppingWalletStore = new ShoppingWalletStore(this.paymentService);
    }
    return this._shoppingWalletStore;
  }

  static getInstance(): Container {
    if (!Container.instance) {
      Container.instance = new Container();
    }
    return Container.instance;
  }

  // Reset method for testing
  reset(): void {
    this._welcomeVideoStore = null;
    this._userRepository = null;
    this._productRepository = null;
    this._orderRepository = null;
    this._repScoreRepository = null;
    this._pay4meRepository = null;
    this._authTokenService = null;
    this._partnerAuthService = null;
    this._otpAuthService = null;
    this._anonymousSessionService = null;
    this._paymentService = null;
    this._checkoutSessionService = null;
    this._pssCalculationService = null;
    this._repScoreService = null;
    this._pay4meService = null;
    this._getProductsUseCase = null;
    this._getPaymentPlansUseCase = null;
    this._createCheckoutSessionUseCase = null;
    this._initializePaymentUseCase = null;
    this._getUserRepScoreUseCase = null;
    this._createPay4MeCampaignUseCase = null;
    this._createOrderUseCase = null;
    this._getOrdersUseCase = null;
    this._partnerAuthenticationUseCase = null;
    this._otpAuthenticationUseCase = null;
    this._anonymousSessionUseCase = null;
    this._brandProductsStore?.reset();
    this._brandProductsStore = null;
    this._collectionStore?.reset();
    this._collectionStore = null;
    this._productStore = null;
    this._orderStore = null;
    this._authStore = null;
    this._paymentStore = null;
    this._checkoutStore = null;
    this._modalStore = null;
    this._repScoreStore = null;
    this._pay4meStore = null;
    this._shoppingWalletStore = null;
  }

  // Generic get method for dependency injection
  get<T>(key: string): T {
    switch (key) {
      case 'GetProductsUseCase':
        return this.getProductsUseCase as T;
      case 'GetPaymentPlansUseCase':
        return this.getPaymentPlansUseCase as T;
      case 'CreateCheckoutSessionUseCase':
        return this.createCheckoutSessionUseCase as T;
      case 'InitializePaymentUseCase':
        return this.initializePaymentUseCase as T;
      case 'GetUserRepScoreUseCase':
        return this.getUserRepScoreUseCase as T;
      case 'CreatePay4MeCampaignUseCase':
        return this.createPay4MeCampaignUseCase as T;
      case 'CreateOrderUseCase':
        return this.createOrderUseCase as T;
      case 'GetOrdersUseCase':
        return this.getOrdersUseCase as T;
      case 'PartnerAuthenticationUseCase':
        return this.partnerAuthenticationUseCase as T;
      case 'OTPAuthenticationUseCase':
        return this.otpAuthenticationUseCase as T;
      case 'AnonymousSessionUseCase':
        return this.anonymousSessionUseCase as T;
      case 'ProductStore':
        return this.productStore as T;
      case 'OrderStore':
        return this.orderStore as T;
      case 'AuthStore':
        return this.authStore as T;
      case 'PaymentStore':
        return this.paymentStore as T;
      case 'CheckoutStore':
        return this.checkoutStore as T;
      case 'ModalStore':
        return this.modalStore as T;
      case 'RepScoreStore':
        return this.repScoreStore as T;
      case 'ShoppingWalletStore':
        return this.shoppingWalletStore as T;
      default:
        throw new Error(`Unknown dependency: ${key}`);
    }
  }
}

// Export singleton instance
export const container = Container.getInstance();
